import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import Link from '@cloudscape-design/components/link';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import { InvoiceTable, type InvoiceTableAction } from '@/components/InvoiceTable';
import { ALL_NFSE_STATUSES, nfsePdfUrl, type Invoice, type NfseStatus, type TenantId } from '@etiquetachef/types';
import { useTenants } from '@/features/tenants/useTenants';
import { TenantPickerTable } from '@/features/tenants/TenantPickerTable';
import {
  subscribeTenantInvoices,
  type InvoiceListResult,
} from '@/lib/firestoreTenantDetail';
import { CancelInvoiceDialog } from '@/features/billing/CancelInvoiceDialog';
import { RecordManualPaymentDialog } from '@/features/billing/RecordManualPaymentDialog';
import { ResendInvoiceEmailDialog } from '@/features/billing/ResendInvoiceEmailDialog';
import { openInvoicePdf } from '@/lib/invoiceDocument';

const TENANT_FILTERS = { status: null } as const;

type OpenAction = { kind: 'pay' | 'cancel' | 'resend'; invoice: Invoice } | null;

/**
 * Tela de faturas: sem tenant, escolhe a empresa; com tenant, lista as
 * faturas dela e abre as ações (baixa manual, cancelar, reenviar, PDF).
 *
 * Origem: sem issue — commit ae81f4d21, 2026-05-05 00:22 +0200 (Sprint 16 —
 * rotas /billing/* + sub-menu Cobrança).
 *
 * Por quê: fatura é subcoleção do tenant, e listar todas cross-tenant
 * exigiria varrer `invoices` por collection-group; a tela parte da empresa
 * (a visão cross-tenant por status fica em `/billing/pending`).
 *
 * Contrato: `/billing/invoices` → `TenantPickerTable` (via `useTenants`,
 * listener de `tenants`, até 200). `/billing/invoices/:tenantId` →
 * `subscribeTenantInvoices` (até 100, `issued_at desc`) + `InvoiceTable`.
 * Ações: `view` navega ao detalhe; `download_pdf` chama a callable
 * `getInvoiceDocumentUrl` (`openInvoicePdf`, link atual do gateway, #776);
 * `download_nfe` abre o PDF público da NFS-e AUTORIZADA (#754); `mark_paid`/
 * `pay_now` → `RecordManualPaymentDialog` (callable `recordManualPayment`,
 * capability `recordManualPayment`: ROOT/FINANCE); `cancel` →
 * `CancelInvoiceDialog` (`cancelInvoice`: ROOT/FINANCE); `resend_email` →
 * `ResendInvoiceEmailDialog` (`resendInvoiceEmail`: ROOT/OPS/FINANCE) —
 * capabilities em `docs/permissions.md`. A tela não grava nada direto.
 *
 * Usado por: `routes/router.tsx` (rotas `billing/invoices` e
 * `billing/invoices/:tenantId`, dentro do `RequireSuperAdmin`), menu
 * Cobrança → "Faturas" (`AdminLayout`); links de `BillingActions`,
 * `BillingOverviewPage`. Só `apps/admin` (web). Spec:
 * `docs/screens/billing-invoices.md`.
 *
 * Arquitetura: leitura pela regra `match /invoices/{invoiceId}` sob
 * `tenants/{tenantId}` (Lê: membro ou super-admin; Escreve: só servidor);
 * `orderBy` de campo único, sem índice composto.
 *
 * Dados: `tenants/{tid}/invoices/{id}` (`InvoiceSchema`) — valores
 * (`total_amount`, `amount_due`, …) em CENTAVOS, formatados pelo
 * `MoneyDisplay`; `status` ∈ PENDING | PAID | OVERDUE | CANCELLED |
 * REFUNDED | VOID; `pdf_url` do Stripe expira (por isso a callable).
 *
 * Dicas: o seletor de empresa depende do `useTenants` (teto de 200) —
 * tenant fora do teto só por URL direta.
 *
 * Issues: #754 (NFS-e), #776 (PDF expirado).
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796).
 */
export function InvoicesPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const params = useParams<{ tenantId?: string }>();
  const tenantId = params.tenantId ?? null;
  const [state, setState] = useState<InvoiceListResult>({ kind: 'LOADING' });
  const [open, setOpen] = useState<OpenAction>(null);

  const tenantsState = useTenants(TENANT_FILTERS);

  useEffect(() => {
    if (!tenantId) {
      setState({ kind: 'READY', invoices: [] });
      return;
    }
    setState({ kind: 'LOADING' });
    return subscribeTenantInvoices(tenantId, setState);
  }, [tenantId]);

  /** Roteia a ação da linha: navegar, abrir PDF/NFS-e ou abrir o diálogo da mutação. */
  function handleAction(invoice: Invoice, action: InvoiceTableAction): void {
    if (!tenantId) return;
    if (action === 'view') {
      void navigate(`/billing/invoices/${tenantId}/${invoice.id}`);
      return;
    }
    if (action === 'download_pdf' && invoice.pdf_url) {
      // #776: link ATUAL do gateway — o `pdf_url` gravado do Stripe expira.
      void openInvoicePdf(tenantId, invoice);
      return;
    }
    // #754 — PDF público da NFS-e do Asaas (só com a nota AUTORIZADA).
    const nfseUrl = action === 'download_nfe' ? nfsePdfUrl(invoice.nfse) : null;
    if (nfseUrl !== null) {
      window.open(nfseUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    if (action === 'mark_paid' || action === 'pay_now') {
      setOpen({ kind: 'pay', invoice });
    } else if (action === 'cancel') {
      setOpen({ kind: 'cancel', invoice });
    } else if (action === 'resend_email') {
      setOpen({ kind: 'resend', invoice });
    }
  }

  const description = tenantId ?? t('billing.invoices.crossTenantHint');

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={description}>
          {t('billing.invoices.title')}
        </Header>
      }
    >
      {!tenantId ? (
        <TenantPickerTable
          tenants={tenantsState.kind === 'READY' ? tenantsState.tenants : []}
          loading={tenantsState.kind === 'LOADING'}
          getHref={(id): string => `/billing/invoices/${id}`}
          title={t('billing.invoices.selectTenant')}
        />
      ) : (
        <SpaceBetween size="l">
          <Link
            href="/billing/invoices"
            onFollow={(e): void => {
              e.preventDefault();
              void navigate('/billing/invoices');
            }}
          >
            {t('billing.invoices.backToCompanies')}
          </Link>
          {state.kind === 'LOADING' ? (
            <Box textAlign="center" padding="l">
              <Spinner size="large" />
            </Box>
          ) : state.kind === 'ERROR' ? (
            <Alert type="error">{t('billing.invoices.errorLoad')}</Alert>
          ) : (
            <InvoiceTable
              invoices={state.invoices}
              locale={i18n.language}
              tone="admin"
              labels={{
                invoiceNumber: t('billing.invoices.columns.invoiceNumber'),
                issuedAt: t('billing.invoices.columns.issuedAt'),
                dueAt: t('billing.invoices.columns.dueAt'),
                total: t('billing.invoices.columns.total'),
                status: t('billing.invoices.columns.status'),
                actions: t('billing.invoices.columns.actions'),
                empty: t('billing.invoices.empty'),
                view: t('billing.invoiceActions.view'),
                downloadPdf: t('billing.invoiceActions.downloadPdf'),
                downloadNfe: t('billing.invoiceActions.downloadNfse'),
                nfse: t('billing.invoices.columns.nfse'),
                nfseStatus: Object.fromEntries(
                  ALL_NFSE_STATUSES.map((s) => [s, t(`billing.invoices.nfseStatus.${s}`)]),
                ) as Record<NfseStatus, string>,
                payNow: t('billing.invoiceActions.markPaid'),
                markPaid: t('billing.invoiceActions.markPaid'),
                cancel: t('billing.invoiceActions.cancel'),
                resendEmail: t('billing.invoiceActions.resendEmail'),
              }}
              onRowAction={handleAction}
            />
          )}
        </SpaceBetween>
      )}

      {tenantId && (
        <>
          <RecordManualPaymentDialog
            open={open?.kind === 'pay'}
            tenantId={tenantId as TenantId}
            invoice={open?.kind === 'pay' ? open.invoice : null}
            onClose={(): void => {
              setOpen(null);
            }}
          />
          <CancelInvoiceDialog
            open={open?.kind === 'cancel'}
            tenantId={tenantId as TenantId}
            invoice={open?.kind === 'cancel' ? open.invoice : null}
            onClose={(): void => {
              setOpen(null);
            }}
          />
          <ResendInvoiceEmailDialog
            open={open?.kind === 'resend'}
            tenantId={tenantId as TenantId}
            invoice={open?.kind === 'resend' ? open.invoice : null}
            onClose={(): void => {
              setOpen(null);
            }}
          />
        </>
      )}
    </ContentLayout>
  );
}
